import { useState, type ReactNode } from 'react';
import { MapPin, RefreshCw } from 'lucide-react';
import type { JobMobileState } from './use-job-mobile';

/**
 * Scan from a phone › Options: the place of capture and Paige's own rescans
 * (docs/engineering/mobile-capture.md, sections 8 and 9). The place is off until an admin turns it on,
 * and turning it on asks them to confirm they have told their people: the law puts that on the company.
 * Each person then agrees or not in the app; without both, no place is kept.
 */
export function MobileSettings({ state }: { state: JobMobileState }): ReactNode {
  const mobile = state.mobile!;
  const [confirming, setConfirming] = useState(false);
  const runsCompany = window.paigeWorkbench?.runsCompany !== false;
  const runsJobs = window.paigeWorkbench?.runsJobs !== false;
  const turn = (on: boolean) =>
    on ? setConfirming(true) : void state.save({ locationEnabled: false });
  return (
    <>
      <Switch
        icon={<MapPin size={20} />}
        title="Location"
        hint={
          mobile.locationEnabled
            ? 'Where each page was scanned, kept 90 days, for people who agree.'
            : 'Off. Only an admin can turn it on.'
        }
        on={mobile.locationEnabled || confirming}
        disabled={state.saving || !runsCompany}
        onChange={turn}
      />
      {confirming && <PlaceConfirm state={state} onDone={() => setConfirming(false)} />}
      <Switch
        icon={<RefreshCw size={20} />}
        title="Rescan"
        hint="When nothing on a page can be read, Paige asks the sender to scan it again."
        on={mobile.autoRescan}
        disabled={state.saving || !runsJobs}
        onChange={(autoRescan) => void state.save({ autoRescan })}
      />
    </>
  );
}

/** What turning the place on means, said before it is turned on. */
function PlaceConfirm({ state, onDone }: { state: JobMobileState; onDone: () => void }): ReactNode {
  return (
    <div className="paige-mobile-confirm" role="alertdialog" aria-label="Turn on location">
      <p>
        Paige keeps where each page was scanned, only at that moment and only for people who agree
        in the app. Your company tells its people why, and answers for it under the laws that apply.
      </p>
      <span>
        <button
          type="button"
          className="paige-email-sheet-button"
          onClick={() => void state.save({ locationEnabled: true }).then(onDone)}
        >
          We have told our people; turn it on
        </button>
        <button type="button" className="quiet-button" onClick={onDone}>
          Cancel
        </button>
      </span>
    </div>
  );
}

function Switch(props: {
  icon: ReactNode;
  title: string;
  hint: string;
  on: boolean;
  disabled: boolean;
  onChange: (on: boolean) => void;
}): ReactNode {
  return (
    <label className="paige-email-sheet-switch paige-phone-option">
      <span className="paige-email-sheet-row-icon" aria-hidden="true">
        {props.icon}
      </span>
      <span>
        <strong>{props.title}</strong>
        <small>{props.hint}</small>
      </span>
      <input
        type="checkbox"
        role="switch"
        checked={props.on}
        disabled={props.disabled}
        onChange={(event) => props.onChange(event.currentTarget.checked)}
      />
    </label>
  );
}
